import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { 
  AppState, 
  ApiProvider, 
  ChatSession, 
  ChatTemplate, 
  ChatMessage,
  ProviderType,
  ChatConfig
} from '@/types';
import { ApiService, ApiTestResult } from '@/services/ApiService';
import { EnhancedConfigParser } from '@/services/EnhancedConfigParser';

interface EnhancedAppStore extends AppState {
  // 供应商管理增强
  addProviderWithTest: (
    provider: Omit<ApiProvider, 'id' | 'createdAt' | 'updatedAt' | 'models'>,
    autoTest?: boolean
  ) => Promise<{ id: string; testResult?: ApiTestResult }>;
  
  updateProviderWithTest: (
    id: string, 
    updates: Partial<ApiProvider>,
    autoTest?: boolean
  ) => Promise<{ success: boolean; testResult?: ApiTestResult }>;
  
  testProviderConnection: (id: string) => Promise<ApiTestResult>;
  batchTestProviders: (ids?: string[]) => Promise<Record<string, ApiTestResult>>;
  
  // 配置解析增强
  parseConfig: (text: string) => ReturnType<typeof EnhancedConfigParser.parse>;
  validateAndCompleteConfig: (config: any) => { valid: boolean; completed: any; errors: string[] };
  
  // 模型管理
  refreshProviderModels: (id: string) => Promise<string[]>;
  setDefaultModel: (providerId: string, model: string) => void;
  
  // 对话增强
  sendChatMessage: (
    providerId: string,
    sessionId: string,
    message: string,
    config?: Partial<ChatConfig>
  ) => Promise<{ success: boolean; response?: any; error?: string }>;
  
  // 状态
  testingProviders: Record<string, boolean>;
  setTestingProvider: (id: string, testing: boolean) => void;
  
  // 统计数据
  providerStats: Record<string, {
    lastTested?: string;
    lastSuccess?: boolean;
    averageLatency?: number;
    testCount: number;
    successCount: number;
  }>;
  updateProviderStats: (id: string, result: ApiTestResult) => void;
  
  // 基础方法（需要从AppState继承但需要实现）
  addProvider: (provider: Omit<ApiProvider, 'id' | 'createdAt' | 'updatedAt'>) => string;
  updateProvider: (id: string, updates: Partial<ApiProvider>) => void;
  deleteProvider: (id: string) => void;
  setCurrentProvider: (id: string | null) => void;
  addSession: (session: Omit<ChatSession, 'id' | 'createdAt' | 'updatedAt'>) => string;
  updateSession: (id: string, updates: Partial<ChatSession>) => void;
  deleteSession: (id: string) => void;
  setCurrentSession: (id: string | null) => void;
}

// 默认配置
const DEFAULT_CHAT_CONFIG: ChatConfig = {
  temperature: 0.7,
  maxTokens: 1000,
  topP: 1,
  frequencyPenalty: 0,
  presencePenalty: 0,
  stream: false,
};

export const useEnhancedAppStore = create<EnhancedAppStore>()(
  persist(
    (set, get) => ({
      // 基础状态
      providers: [],
      sessions: [],
      currentProviderId: null,
      currentSessionId: null,
      templates: [],
      ui: {
        sidebarCollapsed: false,
        activeTab: 'config',
        theme: 'light',
      },
      
      // 增强状态
      testingProviders: {},
      providerStats: {},
      
      /**
       * 添加供应商并测试连接
       */
      addProviderWithTest: async (providerData, autoTest = true) => {
        const id = `provider_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const now = new Date().toISOString();
        
        const provider: ApiProvider = {
          ...providerData,
          id,
          models: [],
          createdAt: now,
          updatedAt: now,
        };
        
        set((state) => ({
          providers: [...state.providers, provider],
        }));
        
        let testResult: ApiTestResult | undefined;
        
        if (autoTest) {
          testResult = await get().testProviderConnection(id);
          
          if (testResult.success && testResult.models) {
            set((state) => ({
              providers: state.providers.map(p =>
                p.id === id
                  ? { ...p, models: testResult!.models || [], updatedAt: new Date().toISOString() }
                  : p
              ),
            }));
          }
        }
        
        // 更新统计
        if (testResult) {
          get().updateProviderStats(id, testResult);
        }
        
        return { id, testResult };
      },
      
      /**
       * 更新供应商并测试连接
       */
      updateProviderWithTest: async (id, updates, autoTest = true) => {
        const state = get();
        const provider = state.providers.find(p => p.id === id);
        
        if (!provider) {
          return { success: false, testResult: undefined };
        }
        
        const updatedProvider = {
          ...provider,
          ...updates,
          updatedAt: new Date().toISOString(),
        };
        
        set((state) => ({
          providers: state.providers.map(p =>
            p.id === id ? updatedProvider : p
          ),
        }));
        
        let testResult: ApiTestResult | undefined;
        
        if (autoTest) {
          testResult = await get().testProviderConnection(id);
          
          if (testResult.success && testResult.models) {
            set((state) => ({
              providers: state.providers.map(p =>
                p.id === id
                  ? { ...p, models: testResult!.models || [], updatedAt: new Date().toISOString() }
                  : p
              ),
            }));
          }
        }
        
        // 更新统计
        if (testResult) {
          get().updateProviderStats(id, testResult);
        }
        
        return { success: true, testResult };
      },
      
      /**
       * 测试供应商连接
       */
      testProviderConnection: async (id: string) => {
        const state = get();
        const provider = state.providers.find(p => p.id === id);
        
        if (!provider) {
          return {
            success: false,
            error: '供应商不存在',
          };
        }
        
        // 设置测试状态
        set((state) => ({
          testingProviders: { ...state.testingProviders, [id]: true },
        }));
        
        try {
          const result = await ApiService.testConnection({
            baseUrl: provider.baseUrl,
            apiKey: provider.apiKey,
            providerType: provider.providerType,
          });
          
          // 如果测试成功且有模型列表，更新供应商
          if (result.success && result.models && result.models.length > 0) {
            set((state) => ({
              providers: state.providers.map(p =>
                p.id === id
                  ? { ...p, models: result.models || [], updatedAt: new Date().toISOString() }
                  : p
              ),
            }));
          }
          
          return result;
        } finally {
          // 清除测试状态
          set((state) => ({
            testingProviders: { ...state.testingProviders, [id]: false },
          }));
        }
      },
      
      /**
       * 批量测试供应商
       */
      batchTestProviders: async (ids?: string[]) => {
        const state = get();
        const providersToTest = ids
          ? state.providers.filter(p => ids.includes(p.id))
          : state.providers;
        
        if (providersToTest.length === 0) {
          return {};
        }
        
        // 设置所有供应商为测试状态
        const testingUpdates = providersToTest.reduce((acc, p) => {
          acc[p.id] = true;
          return acc;
        }, {} as Record<string, boolean>);
        
        set((state) => ({
          testingProviders: { ...state.testingProviders, ...testingUpdates },
        }));
        
        try {
          const results = await ApiService.batchTestConnections(
            providersToTest.map(p => ({
              id: p.id,
              name: p.name,
              baseUrl: p.baseUrl,
              apiKey: p.apiKey,
              providerType: p.providerType,
            }))
          );
          
          // 更新供应商模型列表
          Object.entries(results).forEach(([providerId, result]) => {
            if (result.success && result.models) {
              set((state) => ({
                providers: state.providers.map(p =>
                  p.id === providerId
                    ? { ...p, models: result.models || [], updatedAt: new Date().toISOString() }
                    : p
                ),
              }));
            }
            
            // 更新统计
            get().updateProviderStats(providerId, result);
          });
          
          return results;
        } finally {
          // 清除所有测试状态
          set((state) => ({
            testingProviders: Object.keys(state.testingProviders).reduce((acc, id) => {
              if (!providersToTest.find(p => p.id === id)) {
                acc[id] = state.testingProviders[id];
              }
              return acc;
            }, {} as Record<string, boolean>),
          }));
        }
      },
      
      /**
       * 解析配置文本
       */
      parseConfig: (text: string) => {
        return EnhancedConfigParser.parse(text);
      },
      
      /**
       * 验证和补全配置
       */
      validateAndCompleteConfig: (config: any) => {
        const parsed = EnhancedConfigParser.parse(
          typeof config === 'string' ? config : JSON.stringify(config)
        );
        
        const validation = EnhancedConfigParser.validateConfig(parsed);
        
        if (parsed.baseUrl) {
          parsed.baseUrl = EnhancedConfigParser.cleanAndCompleteUrl(
            parsed.baseUrl,
            parsed.providerType
          );
        }
        
        return {
          valid: validation.valid,
          completed: parsed,
          errors: validation.errors,
        };
      },
      
      /**
       * 刷新供应商模型列表
       */
      refreshProviderModels: async (id: string) => {
        const result = await get().testProviderConnection(id);
        
        if (result.success && result.models) {
          return result.models;
        }
        
        throw new Error(result.error || '无法获取模型列表');
      },
      
      /**
       * 设置默认模型
       */
      setDefaultModel: (providerId: string, model: string) => {
        set((state) => ({
          providers: state.providers.map(p =>
            p.id === providerId
              ? { ...p, defaultModel: model, updatedAt: new Date().toISOString() }
              : p
          ),
        }));
      },
      
      /**
       * 发送聊天消息
       */
      sendChatMessage: async (providerId, sessionId, message, config = {}) => {
        const state = get();
        const provider = state.providers.find(p => p.id === providerId);
        const session = state.sessions.find(s => s.id === sessionId);
        
        if (!provider) {
          return { success: false, error: '供应商不存在' };
        }
        
        if (!session) {
          return { success: false, error: '会话不存在' };
        }
        
        const model = session.model || provider.defaultModel || provider.models[0];
        if (!model) {
          return { success: false, error: '未选择模型' };
        }
        
        const messages: Array<{ role: string; content: string }> = [
          ...session.messages.map(msg => ({
            role: msg.role,
            content: msg.content,
          })),
          { role: 'user', content: message },
        ];
        
        try {
          const response = await ApiService.sendMessage(
            {
              baseUrl: provider.baseUrl,
              apiKey: provider.apiKey,
              providerType: provider.providerType,
            },
            model,
            messages,
            {
              ...DEFAULT_CHAT_CONFIG,
              ...config,
            }
          );
          
          // 提取回复内容
          let assistantReply = '';
          if (response.choices && response.choices[0]?.message?.content) {
            assistantReply = response.choices[0].message.content;
          } else if (response.content && Array.isArray(response.content)) {
            assistantReply = response.content[0]?.text || '';
          } else if (response.candidates && response.candidates[0]?.content?.parts) {
            assistantReply = response.candidates[0].content.parts[0]?.text || '';
          }
          
          // 更新会话
          const now = new Date().toISOString();
          const userMessage: ChatMessage = {
            id: `msg_${Date.now()}_user`,
            role: 'user',
            content: message,
            timestamp: now,
            providerId,
            model,
          };
          
          const assistantMessage: ChatMessage = {
            id: `msg_${Date.now()}_assistant`,
            role: 'assistant',
            content: assistantReply,
            timestamp: now,
            providerId,
            model,
            tokens: response.usage?.total_tokens,
          };
          
          set((state) => ({
            sessions: state.sessions.map(s =>
              s.id === sessionId
                ? {
                    ...s,
                    messages: [...s.messages, userMessage, assistantMessage],
                    messageCount: s.messageCount + 2,
                    tokenCount: s.tokenCount + (response.usage?.total_tokens || 0),
                    lastMessageAt: now,
                    updatedAt: now,
                  }
                : s
            ),
          }));
          
          return { success: true, response };
        } catch (error) {
          return {
            success: false,
            error: error instanceof Error ? error.message : '发送消息失败',
          };
        }
      },
      
      /**
       * 设置测试状态
       */
      setTestingProvider: (id: string, testing: boolean) => {
        set((state) => ({
          testingProviders: { ...state.testingProviders, [id]: testing },
        }));
      },
      
      /**
       * 更新供应商统计
       */
      updateProviderStats: (id: string, result: ApiTestResult) => {
        const now = new Date().toISOString();
        const currentStats = get().providerStats[id] || {
          testCount: 0,
          successCount: 0,
        };
        
        const updatedStats = {
          ...currentStats,
          lastTested: now,
          lastSuccess: result.success,
          testCount: currentStats.testCount + 1,
          successCount: currentStats.successCount + (result.success ? 1 : 0),
        };
        
        if (result.success && result.latency) {
          const prevLatency = currentStats.averageLatency || result.latency;
          updatedStats.averageLatency = Math.round(
            (prevLatency * currentStats.successCount + result.latency) / 
            (currentStats.successCount + 1)
          );
        }
        
        set((state) => ({
          providerStats: { ...state.providerStats, [id]: updatedStats },
        }));
      },
      
      // 基础方法（从原appStore复制）
      addProvider: (provider) => {
        const id = `provider_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const now = new Date().toISOString();
        
        const newProvider: ApiProvider = {
          ...provider,
          id,
          createdAt: now,
          updatedAt: now,
        };
        
        set((state) => ({
          providers: [...state.providers, newProvider],
        }));
        
        return id;
      },
      
      updateProvider: (id, updates) => {
        set((state) => ({
          providers: state.providers.map(p =>
            p.id === id
              ? { ...p, ...updates, updatedAt: new Date().toISOString() }
              : p
          ),
        }));
      },
      
      deleteProvider: (id) => {
        set((state) => ({
          providers: state.providers.filter(p => p.id !== id),
          currentProviderId: state.currentProviderId === id ? null : state.currentProviderId,
        }));
      },
      
      setCurrentProvider: (id) => {
        set({ currentProviderId: id });
      },
      
      setCurrentSession: (id) => {
        set({ currentSessionId: id });
      },
      
      addSession: (session) => {
        const id = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const now = new Date().toISOString();
        
        const newSession: ChatSession = {
          ...session,
          id,
          createdAt: now,
          updatedAt: now,
        };
        
        set((state) => ({
          sessions: [...state.sessions, newSession],
        }));
        
        return id;
      },
      
      updateSession: (id, updates) => {
        set((state) => ({
          sessions: state.sessions.map(s =>
            s.id === id
              ? { ...s, ...updates, updatedAt: new Date().toISOString() }
              : s
          ),
        }));
      },
      
      deleteSession: (id) => {
        set((state) => ({
          sessions: state.sessions.filter(s => s.id !== id),
          currentSessionId: state.currentSessionId === id ? null : state.currentSessionId,
        }));
      },
    }),
    {
      name: 'enhanced-app-store',
      partialize: (state) => ({
        providers: state.providers,
        sessions: state.sessions,
        currentProviderId: state.currentProviderId,
        currentSessionId: state.currentSessionId,
        templates: state.templates,
        providerStats: state.providerStats,
      }),
    }
  )
);